import { ensureLocalDateFormat, getDatesFromTimeRange } from '@/lib/date';
import { Json, TimePrecision } from '@/lib/types';

import { getExplorationType, isModelExploration } from '@/core/exploration';

import {
  DateRangeParameter,
  DateRangeVariableDefinition,
  Exploration,
  ExplorationParameters,
  ModelKind,
  VariableDefinition,
  arrayParameter,
  booleanParameter,
  dateParameter,
  dateRangeParameter,
  numberParameter,
  stringParameter,
  timeIntervalParameter,
} from '../types';

export * from './exploration';
export * from './detail-exploration';
export * from './linking';
export * from './url';
export * from './validation';
export * from './variable';

export const isNumberType = (type: string | null | undefined) =>
  ['Number', 'Float', 'Integer'].includes(type ?? '');

export const isStringType = (type: string | null | undefined) =>
  ['String', 'Enum'].includes(type ?? '');

export const isDateType = (type: string | null | undefined) => type === 'Date';

const isUnparameterizedExploration = (exploration: Exploration) =>
  exploration.parameters.length === 0;

export const getUnparameterizedExplorations = (explorations: Exploration[]) =>
  explorations.filter(isUnparameterizedExploration);

export const isPersistedBaseExploration = (exploration: Exploration) =>
  isUnparameterizedExploration(exploration) && !isModelExploration(exploration);

const getDateRangeInputDefault = ({
  defaultRange,
}: DateRangeVariableDefinition): DateRangeParameter => {
  const precision = TimePrecision.Monthly;
  const { startDate, endDate } = getDatesFromTimeRange(defaultRange, precision);
  return {
    range: defaultRange,
    start: startDate.toISOString(),
    end: endDate.toISOString(),
    precision,
  };
};

export const parseParameterValue = (value: unknown, definition: VariableDefinition) => {
  switch (definition.kind) {
    case 'string':
      return stringParameter.catch(() => definition.defaultValue).parse(value);
    case 'number':
      return numberParameter.catch(() => definition.defaultValue).parse(value);
    case 'enum':
      return stringParameter
        .or(numberParameter)
        .catch(() => definition.defaultValue)
        .parse(value);
    case 'multiselect':
      return arrayParameter.catch(() => definition.defaultValue).parse(value);
    case 'boolean':
      return booleanParameter.catch(() => definition.defaultValue).parse(value);
    case 'date':
      return ensureLocalDateFormat(dateParameter.catch(() => definition.defaultValue).parse(value));
    case 'date_range':
      return dateRangeParameter.catch(() => getDateRangeInputDefault(definition)).parse(value);
    case 'time_interval':
      return timeIntervalParameter.catch(() => definition.defaultValue).parse(value);
  }
};

export const getParameters = (
  urlParameters: Record<string, Json>,
  inputDefinitions: VariableDefinition[],
): ExplorationParameters => {
  return inputDefinitions.reduce((acc, definition) => {
    return {
      ...acc,
      [definition.key]: parseParameterValue(urlParameters[definition.key], definition),
    };
  }, {});
};

export const getExplorationIconName = (exploration: Exploration) => {
  const type = getExplorationType(exploration);

  return type === 'model'
    ? 'Model'
    : type === 'ai'
      ? 'Zap'
      : type === 'exploration'
        ? 'Exploration'
        : 'Hash';
};

export const getModelKindIconName = (kind: ModelKind | null) => {
  switch (kind) {
    case 'Event':
      return 'Event';
    case 'User':
      return 'User';
    case 'Account':
      return 'Briefcase';
    default:
      return 'Box';
  }
};

export const getDataTypeFromValue = (value: Json) => {
  switch (true) {
    case typeof value === 'string':
      return 'String';
    case typeof value === 'number':
      return 'Number';
    case typeof value === 'boolean':
      return 'Boolean';
    case Array.isArray(value):
      return 'Array';
    case typeof value === 'object':
      return 'Object';
    default:
      return null;
  }
};
